import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calculator, Bot, FolderOpen, Receipt, Landmark, Users, Headphones,
  Sparkles, Database, Clock, Check, X, Rocket, Quote, Send,
  Home, MessageSquare, LayoutDashboard, FileText, BarChart3, Percent, Settings as SettingsIcon,
} from 'lucide-react';

const ICONS = {
  Calculator, Bot, FolderOpen, Receipt, Landmark, Users, Headphones, Sparkles, Database, Clock,
};

const SIDEBAR_ICONS = {
  Home, 'AI Chat': MessageSquare, Dashboard: LayoutDashboard, Banking: Landmark,
  Invoices: Receipt, Documents: FolderOpen, Clients: Users, Reports: BarChart3,
  Taxes: Percent, Settings: SettingsIcon,
};

export const DEFAULT_CONTENT = {
  seo: {
    title: 'CountBucks | Accounting, AI and Business Tools in One Platform',
    description: 'Run your accounting, AI, documents, invoicing, banking and business workflows from one connected platform.',
  },
  nav: [
    { label: 'Features', href: '#features' },
    { label: 'AI', href: '#ai' },
    { label: 'Solutions', href: '/features' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Resources', href: '/resources' },
    { label: 'About', href: '#about' },
  ],
  sectionVisibility: { compareStrip: true, features: true, ai: true, comparison: true, testimonials: false, finalCta: true },
  hero: {
    label: 'THE ALL-IN-ONE BUSINESS HUB',
    headline1: 'Stop Juggling Apps.',
    headline2: 'Start Running',
    headline3: 'Your Business.',
    subtext: 'CountBucks combines accounting, AI, documents, invoicing, and banking—all in one platform.',
    pills: ['Save Time', 'Save Money', 'Grow Faster'],
    primaryCta: 'Start Your Free 7-Day Trial',
    primaryCtaHref: '/login?mode=register&plan=starter&cadence=monthly',
    secondaryCta: 'Watch Demo',
    secondaryCtaHref: '/demo',
    trustLine: 'Trusted by Business Owners',
  },
  oldWay: {
    title: 'THE OLD WAY',
    subtitle: 'Multiple Tools. Multiple Logins.',
    items: [
      { name: 'QuickBooks', price: '$35/mo', src: '/logos/quickbooks.png', color: '#2CA01C' },
      { name: 'ChatGPT Plus', price: '$20/mo', src: '/logos/chatgpt.png', color: '#412991' },
      { name: 'Dropbox', price: '$12/mo', src: '/logos/dropbox.png', color: '#0061FF' },
      { name: 'DocuSign', price: '$15/mo', src: '/logos/docusign.png', color: '#4C00FF' },
      { name: 'Document capture', price: '$12/mo', color: '#0EA5E9' },
      { name: 'Time tracker', price: '$10/mo', src: '/logos/toggl.png', color: '#E57CD8' },
      { name: 'Payment software', price: '$15/mo', src: '/logos/stripe.png', color: '#635BFF' },
      { name: 'ClickUp', price: '$10/mo', src: '/logos/clickup.png', color: '#7B68EE' },
      { name: 'Claude', price: '$20/mo', src: '/logos/claude.png', color: '#D97757' },
    ],
    totalLabel: 'Total Cost',
    total: '$149+/mo',
  },
  smartWay: {
    title: 'THE SMART WAY',
    subtitle: 'One Platform. One Login.',
    items: [
      'Accounting & Bookkeeping', '240+ AI Models in One Chat', 'Document Management & OCR',
      'Invoicing & Payments', 'eSignatures', 'Bank Reconciliation', 'Client Portal',
      'Time Tracking', 'Priority Support',
    ],
    totalLabel: 'Total Cost',
    total: '$19.99',
    totalSuffix: '/mo',
  },
  compareStrip: {
    label: "Every tool you're paying for — already inside CountBucks.",
    items: [
      { name: 'ChatGPT', src: '/logos/chatgpt.png' },
      { name: 'Claude', src: '/logos/claude.png' },
      { name: 'Gemini', src: '/logos/gemini.png' },
      { name: 'QuickBooks', src: '/logos/quickbooks.png' },
      { name: 'Xero', src: '/logos/xero.png' },
      { name: 'Dropbox', src: '/logos/dropbox.png' },
      { name: 'DocuSign', src: '/logos/docusign.png' },
      { name: 'Stripe', src: '/logos/stripe.png' },
    ],
  },
  featuresHeading: {
    part1: 'Everything ', green1: 'Connected.', part2: ' Everything ', orange: 'Automated.', part3: ' Everything ', green2: 'You Need.',
  },
  features: [
    { title: 'Accounting', desc: 'Powerful bookkeeping and financials', icon: 'Calculator', color: '#27B84A', href: '/features' },
    { title: 'AI Assistant', desc: 'Access 240+ AI models trained on your data', icon: 'Bot', color: '#8B5CF6', href: '/features' },
    { title: 'Documents', desc: 'Store, organize & find everything instantly', icon: 'FolderOpen', color: '#3B82F6', href: '/features' },
    { title: 'Invoicing', desc: 'Create, send & get paid faster', icon: 'Receipt', color: '#FF7816', href: '/features' },
    { title: 'Banking', desc: 'Connect & reconcile all your accounts', icon: 'Landmark', color: '#14B8A6', href: '/features' },
    { title: 'Client Portal', desc: 'Collaborate securely with clients', icon: 'Users', color: '#EAB308', href: '/features' },
  ],
  aiSection: {
    sidebar: ['Home', 'AI Chat', 'Dashboard', 'Banking', 'Invoices', 'Documents', 'Clients', 'Reports', 'Taxes', 'Settings'],
    heading: 'What do you need today?',
    messages: [
      { role: 'user', text: 'How much did I spend on restaurants last month?' },
      { role: 'ai', text: 'You spent $1,624.52 on restaurants in March, which is 18% less than February.' },
      { role: 'user', text: "Which customers haven't paid me?" },
      { role: 'ai', text: 'You have three customers with unpaid invoices totaling $4,250.' },
      { role: 'user', text: 'What expenses should I deduct?' },
      { role: 'ai', text: 'Based on your business, here are seven common deductions you may be missing.' },
      { role: 'user', text: 'Build me a cash flow forecast.' },
      { role: 'ai', text: 'Here is your six-month cash flow forecast based on current data.' },
    ],
    label: 'AI THAT UNDERSTANDS YOUR BUSINESS',
    headline1: 'One Chat.',
    headline2: 'Every Answer.',
    subtext: 'CountBucks connects to your financial data so AI can give you real insights, not generic answers.',
    benefits: [
      { title: '240+ AI models in one place', desc: 'ChatGPT, Claude, Gemini, Perplexity & more', icon: 'Sparkles' },
      { title: 'Knows your business', desc: 'AI with real-time access to your numbers', icon: 'Database' },
      { title: 'Works for you 24/7', desc: 'Get answers, reports & recommendations instantly', icon: 'Clock' },
    ],
    disclaimer: 'AI responses are informational and are not a substitute for individualized professional tax, legal, or financial advice.',
  },
  comparison: {
    label: 'THE CLEAR CHOICE',
    headline1: 'More Features.',
    headline2: 'Better Price.',
    subtext: 'CountBucks gives you more for a fraction of the cost.',
    cta: 'Start Your Free 7-Day Trial',
    ctaNote: 'Cancel before day 8 and pay $0.',
    columns: ['CountBucks', 'QuickBooks', 'Xero'],
    prices: ['$19.99', '$35+', '$25+'],
    rows: [
      { feature: 'Accounting & Bookkeeping', values: [true, true, true] },
      { feature: 'AI (240+ Models)', values: [true, false, false] },
      { feature: 'Document Management', values: [true, false, false] },
      { feature: 'Invoicing & Payments', values: [true, true, true] },
      { feature: 'Bank Reconciliation', values: [true, true, true] },
      { feature: 'Client Portal', values: [true, false, false] },
      { feature: 'eSignatures', values: [true, false, false] },
      { feature: 'Time Tracking', values: [true, false, false] },
    ],
    disclaimer: 'Competitor pricing and features may change.',
    lastVerified: '2026-06-01',
  },
  testimonials: [
    { quote: 'CountBucks replaced 7 different tools. We save over $1,200 a year and hours every week.', name: 'Sarah J.', role: 'CEO of Marketing Agency', avatar: '/avatars/sarah.jpg' },
    { quote: "The AI insights are incredible. It's like having a CFO in your pocket 24/7.", name: 'Michael T.', role: 'Ecommerce Founder', avatar: 'https://randomuser.me/api/portraits/men/32.jpg' },
    { quote: 'My CPA has direct access and everything is organized. Finally, so simple!', name: 'Jessica L.', role: 'Real Estate Investor', avatar: 'https://randomuser.me/api/portraits/women/68.jpg' },
  ],
  finalCta: {
    headline: 'Ready to simplify your business?',
    subtext: 'Join business owners who run their business all in one place.',
    button: 'Start Free Trial Today',
    buttonHref: '/login?mode=register&plan=starter&cadence=monthly',
  },
  footer: {
    tagline: 'Accounting, AI and business tools in one platform.',
    columns: [
      { title: 'Product', links: [{ label: 'Features', href: '/features' }, { label: 'Pricing', href: '#pricing' }, { label: 'Watch Demo', href: '/demo' }, { label: 'Client Portal', href: '/portal' }] },
      { title: 'Resources', links: [{ label: 'CountBucks University', href: '/resources' }, { label: 'Support', href: '/contact' }, { label: 'Security', href: '/security' }] },
      { title: 'Company', links: [{ label: 'About', href: '#about' }, { label: 'Contact', href: '/contact' }, { label: 'Classic Site', href: '/landing-classic' }] },
      { title: 'Legal', links: [{ label: 'Privacy Policy', href: '/privacy' }, { label: 'Terms of Service', href: '/terms' }, { label: 'Security', href: '/security' }] },
    ],
    disclaimer: 'CountBucks provides software and general information, not individualized tax, legal, or financial advice. AI-generated responses are informational only. Consult a qualified professional regarding your specific situation. Banking services are provided by partner institutions.',
  },
};

export function BrandIcon({ name, slug, src, color = '#64748B', plain = false }) {
  const [failed, setFailed] = useState(!slug && !src);
  if (failed) {
    return (
      <span
        className={`flex shrink-0 items-center justify-center font-black text-white ${plain ? 'h-5 w-5 rounded-md text-[10px]' : 'h-7 w-7 rounded-lg text-[11px]'}`}
        style={{ backgroundColor: color }}
        aria-hidden="true"
      >
        {name.charAt(0)}
      </span>
    );
  }
  const url = src || `https://cdn.simpleicons.org/${slug}/${color.replace('#', '')}`;
  if (plain) {
    return <img src={url} alt="" loading="lazy" className="h-5 w-5 rounded-sm" onError={() => setFailed(true)} />;
  }
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-black/5 bg-white dark:border-white/10 dark:bg-white/95">
      <img src={url} alt="" loading="lazy" className="h-[18px] w-[18px] rounded-sm" onError={() => setFailed(true)} />
    </span>
  );
}

export function FeaturesGrid({ content, fade, onAnchor }) {
  const h = content.featuresHeading;
  return (
    <section id="features" className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="features-section">
      <motion.h2 {...fade(0)} className="mx-auto max-w-4xl text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
        {h.part1}<span className="text-[#27B84A]">{h.green1}</span>
        {h.part2}<span className="text-[#FF7816]">{h.orange}</span>
        {h.part3}<span className="text-[#27B84A]">{h.green2}</span>
      </motion.h2>
      <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
        {content.features.map((f, i) => {
          const Icon = ICONS[f.icon] || Calculator;
          return (
            <motion.a
              key={f.title}
              {...fade(0.05 * i)}
              href={f.href}
              onClick={(e) => onAnchor(e, f.href)}
              data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="group flex flex-col items-center rounded-2xl border border-[#E4E8E6] bg-white p-5 text-center shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-black/5 dark:border-[#1C2420] dark:bg-[#10150F]"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: `${f.color}1A` }}>
                <Icon className="h-6 w-6" style={{ color: f.color }} />
              </span>
              <span className="mt-3 text-sm font-bold">{f.title}</span>
              <span className="mt-1 text-xs leading-relaxed text-[#596273] dark:text-[#8B948F]">{f.desc}</span>
            </motion.a>
          );
        })}
      </div>
    </section>
  );
}

export function AIDemoSection({ ai, fade, reduceMotion }) {
  return (
    <section id="ai" className="bg-[#F7F9F8] py-20 dark:bg-[#0D120E]" data-testid="ai-demo-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        {/* Product mockup */}
        <motion.div {...fade(0)} className="overflow-hidden rounded-3xl border border-[#E4E8E6] bg-white shadow-xl shadow-black/5 dark:border-[#1C2420] dark:bg-[#10150F]">
          <div className="flex">
            <aside className="hidden w-44 shrink-0 border-r border-[#E4E8E6] p-3 sm:block dark:border-[#1C2420]">
              <div className="flex items-center gap-1.5 px-2 pb-3">
                <img src="/cb-logo.png" alt="" className="h-5 w-5" />
                <span className="text-[10px] font-extrabold tracking-[0.12em] text-[#118A35] dark:text-[#27B84A]">COUNTBUCKS</span>
              </div>
              {ai.sidebar.map((item) => {
                const Icon = SIDEBAR_ICONS[item] || Home;
                const active = item === 'AI Chat';
                return (
                  <div key={item} className={`mt-0.5 flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium ${active ? 'bg-[#27B84A] text-white' : 'text-[#596273] dark:text-[#8B948F]'}`}>
                    <Icon className="h-3.5 w-3.5" /> {item}
                  </div>
                );
              })}
            </aside>
            <div className="flex min-h-[480px] flex-1 flex-col p-4 sm:p-5">
              <p className="text-lg font-bold">{ai.heading}</p>
              <div className="mt-4 flex-1 space-y-3" data-testid="ai-chat-messages">
                {ai.messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.4, delay: reduceMotion ? 0 : 0.3 * i }}
                    className={m.role === 'user' ? 'flex justify-end' : 'flex items-start gap-2'}
                  >
                    {m.role === 'ai' && <img src="/cb-logo.png" alt="" className="mt-1 h-5 w-5 shrink-0" />}
                    <div className={m.role === 'user'
                      ? 'max-w-[85%] rounded-2xl rounded-br-md bg-[#2563EB] px-3.5 py-2 text-xs font-medium text-white'
                      : 'max-w-[85%] rounded-2xl rounded-tl-md bg-[#F1F4F2] px-3.5 py-2 text-xs text-[#18202D] dark:bg-[#1A211C] dark:text-[#D9DED9]'}
                    >
                      {m.text}
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl border border-[#E4E8E6] px-3.5 py-2.5 dark:border-[#1C2420]">
                <span className="flex-1 text-xs text-[#9AA2AE]">Ask anything about your business…</span>
                <Send className="h-4 w-4 text-[#27B84A]" />
              </div>
            </div>
          </div>
        </motion.div>
        {/* Copy */}
        <motion.div {...fade(0.1)}>
          <span className="inline-block rounded-full border border-[#27B84A]/40 bg-[#F2FBF4] px-4 py-1.5 text-xs font-bold tracking-widest text-[#118A35] dark:bg-[#0E1A11] dark:text-[#27B84A]">{ai.label}</span>
          <h2 className="mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl">
            {ai.headline1}<br />{ai.headline2}
          </h2>
          <p className="mt-4 max-w-md text-base text-[#596273] sm:text-lg dark:text-[#AEB6B1]">{ai.subtext}</p>
          <ul className="mt-7 space-y-5">
            {ai.benefits.map((b) => {
              const Icon = ICONS[b.icon] || Sparkles;
              return (
                <li key={b.title} className="flex items-start gap-4" data-testid={`ai-benefit-${b.icon.toLowerCase()}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#27B84A]/10">
                    <Icon className="h-5 w-5 text-[#118A35] dark:text-[#27B84A]" />
                  </span>
                  <div>
                    <p className="font-bold">{b.title}</p>
                    <p className="text-sm text-[#596273] dark:text-[#8B948F]">{b.desc}</p>
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="mt-7 max-w-md text-xs italic text-[#9AA2AE] dark:text-[#6B7570]">{ai.disclaimer}</p>
        </motion.div>
      </div>
    </section>
  );
}

function CellMark({ ok }) {
  return ok
    ? <span className="mx-auto flex h-5 w-5 items-center justify-center rounded-full bg-[#27B84A]"><Check className="h-3 w-3 text-white" strokeWidth={3} /></span>
    : <X className="mx-auto h-4 w-4 text-[#C4CBC7] dark:text-[#3A443E]" />;
}

export function ComparisonSection({ cmp, fade }) {
  return (
    <section id="pricing" className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="comparison-section">
      <div className="grid gap-10 rounded-3xl border border-[#E4E8E6] bg-[#F7F9F8] p-6 sm:p-10 lg:grid-cols-[minmax(240px,1fr)_2.4fr] dark:border-[#1C2420] dark:bg-[#0D120E]">
        <motion.div {...fade(0)}>
          <span className="inline-block rounded-full border border-[#FF7816]/40 bg-[#FF7816]/10 px-4 py-1.5 text-xs font-bold tracking-widest text-[#FF7816]">{cmp.label}</span>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl">{cmp.headline1}<br />{cmp.headline2}</h2>
          <p className="mt-4 text-base text-[#596273] dark:text-[#AEB6B1]">{cmp.subtext}</p>
          <Link
            to="/login?mode=register&plan=starter&cadence=monthly"
            data-testid="comparison-cta"
            className="mt-6 inline-flex rounded-2xl bg-[#27B84A] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-600/25 transition-colors hover:bg-[#118A35]"
          >
            {cmp.cta}
          </Link>
          {cmp.ctaNote ? <p className="mt-2.5 text-xs text-[#9AA2AE]">{cmp.ctaNote}</p> : null}
        </motion.div>
        <motion.div {...fade(0.1)} className="overflow-x-auto">
          <table className="w-full min-w-[540px] border-separate border-spacing-0 text-sm" data-testid="comparison-table">
            <thead>
              <tr>
                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wider text-[#596273] dark:text-[#8B948F]">Features</th>
                {cmp.columns.map((c, i) => (
                  <th key={c} className={`pb-3 text-center text-xs font-extrabold uppercase tracking-wider ${i === 0 ? 'text-[#118A35] dark:text-[#27B84A]' : 'text-[#596273] dark:text-[#8B948F]'}`}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cmp.rows.map((row, ri) => (
                <tr key={row.feature}>
                  <td className={`border-t border-[#E4E8E6] py-2.5 pr-4 font-medium dark:border-[#1C2420] ${ri === 0 ? '' : ''}`}>{row.feature}</td>
                  {row.values.map((v, vi) => (
                    <td key={vi} className={`border-t border-[#E4E8E6] py-2.5 text-center dark:border-[#1C2420] ${vi === 0 ? 'bg-[#27B84A]/10 dark:bg-[#27B84A]/10' : ''}`}>
                      <CellMark ok={v} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="border-t border-[#E4E8E6] py-3.5 pr-4 text-xs font-bold uppercase tracking-wider text-[#596273] dark:border-[#1C2420] dark:text-[#8B948F]">Monthly Price</td>
                {cmp.prices.map((p, i) => (
                  <td key={i} className={`border-t border-[#E4E8E6] py-3.5 text-center text-lg font-extrabold dark:border-[#1C2420] ${i === 0 ? 'rounded-b-xl bg-[#27B84A]/10 text-[#118A35] dark:text-[#27B84A]' : 'text-[#18202D] dark:text-white'}`}>
                    {p}<span className="text-[10px] font-semibold text-[#9AA2AE]">/mo</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <p className="mt-4 text-xs italic text-[#9AA2AE]" data-testid="comparison-disclaimer">
            {cmp.disclaimer}{cmp.lastVerified ? ` Last verified: ${cmp.lastVerified}.` : ''}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

export function TestimonialsCTA({ testimonials, cta, fade, showTestimonials = true, showCta = true }) {
  return (
    <section id="about" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6" data-testid="testimonials-section">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {showTestimonials && testimonials.map((t, i) => (
          <motion.figure
            key={t.name}
            {...fade(0.06 * i)}
            data-testid={`testimonial-${i}`}
            className="flex flex-col rounded-3xl border border-[#E4E8E6] bg-white p-6 shadow-sm dark:border-[#1C2420] dark:bg-[#10150F]"
          >
            <Quote className="h-6 w-6 fill-[#27B84A] text-[#27B84A]" />
            <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-[#18202D] dark:text-[#D9DED9]">{t.quote}</blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
              <img src={t.avatar} alt={t.name} loading="lazy" className="h-10 w-10 rounded-full object-cover" />
              <div>
                <p className="text-sm font-bold">{t.name}</p>
                <p className="text-xs text-[#596273] dark:text-[#8B948F]">{t.role}</p>
              </div>
            </figcaption>
          </motion.figure>
        ))}
        {showCta && (
        <motion.div
          {...fade(0.2)}
          data-testid="final-cta-panel"
          className="relative flex flex-col justify-center overflow-hidden rounded-3xl bg-[#118A35] p-7 text-white md:col-span-2 xl:col-span-1"
        >
          <span className="absolute -right-6 -top-6 flex h-24 w-24 items-center justify-center rounded-full bg-white/10">
            <Rocket className="h-10 w-10 -rotate-45 text-[#FF9A4D]" />
          </span>
          <h3 className="text-2xl font-extrabold leading-tight">{cta.headline}</h3>
          <p className="mt-3 text-sm text-white/85">{cta.subtext}</p>
          <Link
            to={cta.buttonHref}
            data-testid="final-cta-btn"
            className="mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-white px-5 py-3 text-sm font-bold text-[#118A35] transition-transform hover:-translate-y-0.5 sm:w-auto"
          >
            {cta.button}
          </Link>
        </motion.div>
        )}
      </div>
    </section>
  );
}

export function HubFooter({ footer, onAnchor }) {
  return (
    <footer className="border-t border-[#E4E8E6] bg-[#F7F9F8] dark:border-[#1C2420] dark:bg-[#0D120E]" data-testid="hub-footer">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <div className="grid gap-10 md:grid-cols-[1.4fr_repeat(4,1fr)]">
          <div>
            <span className="inline-flex items-center gap-2">
              <img src="/cb-logo.png" alt="CountBucks logo" className="h-8 w-8" />
              <span className="text-sm font-extrabold tracking-[0.18em] text-[#118A35] dark:text-[#27B84A]">COUNTBUCKS</span>
            </span>
            <p className="mt-3 max-w-xs text-sm text-[#596273] dark:text-[#8B948F]">{footer.tagline}</p>
          </div>
          {footer.columns.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <p className="text-xs font-bold uppercase tracking-wider text-[#18202D] dark:text-white">{col.title}</p>
              <ul className="mt-3 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      onClick={(e) => onAnchor(e, l.href)}
                      className="text-sm text-[#596273] transition-colors hover:text-[#118A35] dark:text-[#8B948F] dark:hover:text-[#27B84A]"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="mt-12 border-t border-[#E4E8E6] pt-6 dark:border-[#1C2420]">
          <p className="text-xs leading-relaxed text-[#9AA2AE] dark:text-[#6B7570]">{footer.disclaimer}</p>
          <p className="mt-3 text-xs text-[#9AA2AE] dark:text-[#6B7570]">© {new Date().getFullYear()} CountBucks. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
